import { useCallback, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError, api, errorMessage } from '../../../lib/api';
import { organizationKeys } from './api';
import type { Company, CompanyField } from './types';

type Values = Record<CompanyField, string>;

function valuesOf(company: Company): Values {
  return {
    companyName: company.companyName ?? '',
    legalForm: company.legalForm ?? '',
    street: company.street ?? '',
    postalCodeCity: company.postalCodeCity,
    register: company.register,
    vatId: company.vatId ?? '',
    managingDirectors: company.managingDirectors ?? '',
    supervisoryAuthority: company.supervisoryAuthority ?? '',
    senderName: company.senderName ?? '',
    phone: company.phone ?? '',
    contactEmail: company.contactEmail ?? '',
  };
}

export interface CompanyForm {
  values: Values;
  errors: Partial<Record<CompanyField, string>>;
  failure: string | null;
  checking: boolean;
  set: (field: CompanyField, value: string) => void;
  /** Saves the field when it changed (`PATCH`, the VAT ID through the check); resolves true on success. */
  commit: (field: CompanyField, value?: string) => Promise<boolean>;
  /** „USt-ID erneut prüfen". */
  checkVat: () => Promise<void>;
  /** Waits for every save in flight (before „Weiter"). */
  settle: () => Promise<void>;
}

/**
 * Field-level auto-save of the company data (Admin 04, Einstellungen 20 „Absender und Anschrift"): every field is
 * saved on blur with `PATCH /settings/organization/company`; a changed VAT ID is saved and checked in one call
 * (`POST …/vat-check`). Server field errors stay under their field.
 */
export function useCompanyForm(company: Company, onSaved?: () => void): CompanyForm {
  const queryClient = useQueryClient();
  const [values, setValues] = useState<Values>(() => valuesOf(company));
  const [errors, setErrors] = useState<Partial<Record<CompanyField, string>>>({});
  const [failure, setFailure] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const saved = useRef<Values>(valuesOf(company));
  const inFlight = useRef(new Set<Promise<unknown>>());

  const accept = useCallback(
    (next: Company, field: CompanyField | null) => {
      queryClient.setQueryData(organizationKeys.company, next);
      const fresh = valuesOf(next);
      if (field) {
        saved.current = { ...saved.current, [field]: fresh[field] };
        setValues((current) => ({ ...current, [field]: fresh[field] }));
      } else {
        saved.current = fresh;
        setValues(fresh);
      }
      onSaved?.();
    },
    [queryClient, onSaved],
  );

  const track = useCallback(<T>(promise: Promise<T>): Promise<T> => {
    inFlight.current.add(promise);
    void promise.finally(() => inFlight.current.delete(promise)).catch(() => undefined);
    return promise;
  }, []);

  const fail = useCallback((field: CompanyField, error: unknown) => {
    if (error instanceof ApiError && error.fields[field]) {
      const message = error.fields[field];
      setErrors((current) => ({ ...current, [field]: message }));
    } else {
      setFailure(errorMessage(error));
    }
  }, []);

  const commit = useCallback(
    async (field: CompanyField, explicit?: string): Promise<boolean> => {
      const raw = explicit ?? values[field];
      const value = raw.trim();
      if (value === saved.current[field].trim()) return true;
      setFailure(null);
      setErrors((current) => {
        const copy = { ...current };
        delete copy[field];
        return copy;
      });
      try {
        if (field === 'vatId' && value !== '') {
          setChecking(true);
          const next = await track(
            api<Company>('/settings/organization/company/vat-check', {
              method: 'POST',
              body: { vatId: value },
            }),
          );
          accept(next, field);
        } else {
          const next = await track(
            api<Company>('/settings/organization/company', {
              method: 'PATCH',
              body: { [field]: value === '' ? null : value },
            }),
          );
          accept(next, field);
        }
        return true;
      } catch (error) {
        fail(field, error);
        return false;
      } finally {
        if (field === 'vatId') setChecking(false);
      }
    },
    [values, track, accept, fail],
  );

  const checkVat = useCallback(async () => {
    setFailure(null);
    setChecking(true);
    const value = values.vatId.trim();
    try {
      const next = await track(
        api<Company>('/settings/organization/company/vat-check', {
          method: 'POST',
          body: value !== '' && value !== saved.current.vatId.trim() ? { vatId: value } : {},
        }),
      );
      setErrors((current) => {
        const copy = { ...current };
        delete copy.vatId;
        return copy;
      });
      accept(next, 'vatId');
    } catch (error) {
      fail('vatId', error);
    } finally {
      setChecking(false);
    }
  }, [values.vatId, track, accept, fail]);

  const settle = useCallback(async () => {
    await Promise.allSettled([...inFlight.current]);
  }, []);

  const set = useCallback((field: CompanyField, value: string) => {
    setValues((current) => ({ ...current, [field]: value }));
  }, []);

  return { values, errors, failure, checking, set, commit, checkVat, settle };
}
